import { IntlShape, MessageDescriptor, defineMessages } from 'react-intl';

import {
  DEFAULT_FIND_COUNT,
  DEFAULT_FIND_DEPTH_CM,
  DEFAULT_FIND_SITUATION_TYPE,
} from '../../consts';
import { VocabularyKind } from '../../i18n';
import { AmcrPasFile, toPoint } from '../AmcrPasDialog';

/** Matches the `PasFindFields` GraphQL fragment. */
export interface PasFind {
  id: string;
  evId?: string | null;
  siteId?: string | null;
  description?: string | null;
  foundAt?: string | null;
  material?: string | null;
  era?: string | null;
  type?: string | null;
  localization?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  depth?: number | null;
  count?: number | null;
  situationType?: string | null;
  foundBy?: string | null;
  visibility?: string | null;
  image?: string | null;
  images?: string[] | null;
  amcrId?: string | null;
  amcrStatus?: string | null;
  amcrData?: {
    images?: string[] | null;
    files?: AmcrPasFile[] | null;
  } | null;
}

export type PasFieldKey =
  | 'type'
  | 'material'
  | 'era'
  | 'foundAt'
  | 'localization'
  | 'coordinates';

export interface PasCheckedValues {
  type?: string | null;
  material?: string | null;
  era?: string | null;
  foundAt?: string | null;
  localization?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

type Vocabulary = (kind: VocabularyKind, value?: string | null) => string;

/** Acolyte find type that has no PAS equivalent. */
const UNKNOWN_FIND_TYPE = 'unknown';

export const pasFieldLabels: Record<
  PasFieldKey,
  MessageDescriptor
> = defineMessages({
  type: { id: 'find.field.type', defaultMessage: 'Find type' },
  material: { id: 'find.field.material', defaultMessage: 'Material' },
  era: { id: 'find.field.era', defaultMessage: 'Era' },
  foundAt: { id: 'find.field.dateFound', defaultMessage: 'Date found' },
  localization: {
    id: 'find.field.localization',
    defaultMessage: 'Localization',
  },
  coordinates: {
    id: 'findPasEdit.field.coordinates',
    defaultMessage: 'Coordinates',
  },
});

export const situationTypeMessages: Record<
  string,
  MessageDescriptor
> = defineMessages({
  field: { id: 'find.situationType.field', defaultMessage: 'Topsoil' },
  pasture: {
    id: 'find.situationType.pasture',
    defaultMessage: 'Pasture/meadow',
  },
  forest: { id: 'find.situationType.forest', defaultMessage: 'Forest litter' },
  'natural topsoil': {
    id: 'find.situationType.naturalTopsoil',
    defaultMessage: 'Natural soil horizon',
  },
  'cultural layer': {
    id: 'find.situationType.culturalLayer',
    defaultMessage: 'Cultural layer/feature',
  },
  subsoil: {
    id: 'find.situationType.subsoil',
    defaultMessage: 'Subsoil/bedrock',
  },
  surface: { id: 'find.situationType.surface', defaultMessage: 'Surface' },
  water: {
    id: 'find.situationType.water',
    defaultMessage: 'Water body/stream',
  },
  building: { id: 'find.situationType.building', defaultMessage: 'Building' },
  other: { id: 'find.situationType.other', defaultMessage: 'Other context' },
});

const situationMessages = defineMessages({
  depth: { id: 'findPas.situation.depth', defaultMessage: '{depth} cm' },
  count: {
    id: 'findPas.situation.count',
    defaultMessage: '{count, plural, one {# pc} other {# pcs}}',
  },
});

const toCoordinateText = (value: number | string | null | undefined) =>
  value == null ? '' : String(value);

const lowerFirst = (value: string) =>
  value.charAt(0).toLocaleLowerCase() + value.slice(1);

export const getMissingPasFields = (
  values: PasCheckedValues
): PasFieldKey[] => {
  const point = toPoint(
    toCoordinateText(values.latitude),
    toCoordinateText(values.longitude)
  );
  const checks: Array<[PasFieldKey, boolean]> = [
    ['type', !values.type || values.type === UNKNOWN_FIND_TYPE],
    ['material', !values.material],
    ['era', !values.era],
    ['foundAt', !values.foundAt],
    ['localization', !values.localization?.trim()],
    ['coordinates', !point],
  ];

  return checks.filter(([, isMissing]) => isMissing).map(([key]) => key);
};

export const getSituationTypeLabel = (
  intl: IntlShape,
  situationType: string
) => {
  const message = situationTypeMessages[situationType];

  return message ? intl.formatMessage(message) : situationType;
};

/** E.g. "bronze spur, early Roman period". */
export const getPasFindTitle = (
  vocabulary: Vocabulary,
  { type, material, era }: Pick<PasCheckedValues, 'type' | 'material' | 'era'>
) => {
  const name = [vocabulary('material', material), vocabulary('findType', type)]
    .filter(Boolean)
    .map(lowerFirst)
    .join(' ');
  const eraLabel = lowerFirst(vocabulary('era', era));

  return [name, eraLabel].filter(Boolean).join(', ');
};

/** E.g. "topsoil, 20 cm, 1 pc", with the PAS export defaults applied. */
export const getPasSituationSummary = (
  intl: IntlShape,
  { situationType, depth, count }: PasFind
) =>
  [
    lowerFirst(
      getSituationTypeLabel(intl, situationType || DEFAULT_FIND_SITUATION_TYPE)
    ),
    intl.formatMessage(situationMessages.depth, {
      depth: depth ?? DEFAULT_FIND_DEPTH_CM,
    }),
    intl.formatMessage(situationMessages.count, {
      count: count ?? DEFAULT_FIND_COUNT,
    }),
  ].join(', ');
